import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'constants/AppRoutes';
import { COMPANY_MAP_TABS } from 'constants/interfaces';
import { useAiScreenActions } from 'contexts/AiScreenActionsContext';
import { useAppDispatch } from 'store/hooks/useApp';
import { setValueChainMapState } from 'store/slices/valueChainMap/slice';

import type { IOpenMonitoringSessionAction } from 'utils/interfaces/IAiScreenAction';

/** What the caller says: which session of which map, and what of it to open. The type is implied. */
export type TOpenMonitoringSession = Omit<IOpenMonitoringSessionAction, 'type'>;

/**
 * Opens a monitoring session of any agent from wherever the user is: its results, or the session
 * in the state it is in.
 *
 * The same route as `useOpenAgentSession`: the map's value chain tab answers the action when it
 * is mounted; when it is not, the action waits and the app navigates to the map's value chain
 * tab, which takes the action as it mounts.
 */
const useOpenMonitoringSession = (): (request: TOpenMonitoringSession) => void => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { dispatch: dispatchScreenAction } = useAiScreenActions();

  return useCallback((request: TOpenMonitoringSession) => {
    if (dispatchScreenAction({ type: 'openMonitoringSession', ...request })) {
      return;
    }

    dispatch(setValueChainMapState({ currentCompanyMapTab: COMPANY_MAP_TABS.VALUE_CHAIN_MAP }));
    navigate(AppRoutes.companyMap.replace(':valueChainMapId', `${request.valueChainMapId}`));
  }, [dispatch, dispatchScreenAction, navigate]);
};

export default useOpenMonitoringSession;
